import { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import AdminLayout from '../../components/layout/AdminLayout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../../components/ui/dialog';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '../../components/ui/accordion';
import { 
  FileText, 
  Plus,
  Edit,
  Trash2,
  Shield,
  Loader2,
  ChevronRight,
  Lock,
  CheckCircle,
  AlertCircle
} from 'lucide-react';
import { toast } from 'sonner';
import { getUserErrorMessage } from '../../utils/errorMessages';

const AdminFrameworks = () => {
  const { api } = useAuth();
  const [loading, setLoading] = useState(true);
  const [frameworks, setFrameworks] = useState([]);
  const [selectedFramework, setSelectedFramework] = useState(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showControlModal, setShowControlModal] = useState(false);
  const [editingControl, setEditingControl] = useState(null);
  const [saving, setSaving] = useState(false);
  
  // Form state
  const [formData, setFormData] = useState({
    name: '',
    version: '',
    description: ''
  });
  
  const [controlForm, setControlForm] = useState({
    code: '',
    name: '',
    category: '',
    description: ''
  });

  useEffect(() => {
    fetchFrameworks();
  }, []);

  const fetchFrameworks = async () => {
    try {
      const response = await api.get('/admin/frameworks');
      setFrameworks(response.data);
    } catch (error) {
      toast.error('Frameworks laden mislukt');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateFramework = async () => {
    if (!formData.name || !formData.version || !formData.description) {
      toast.error('Vul alle velden in');
      return;
    }

    setSaving(true);
    try {
      await api.post('/admin/frameworks', formData);
      toast.success('Framework aangemaakt');
      setShowCreateModal(false);
      resetForm();
      fetchFrameworks();
    } catch (error) {
      toast.error(getUserErrorMessage(error, 'Aanmaken mislukt'));
    } finally {
      setSaving(false);
    }
  };

  const handleUpdateFramework = async () => {
    if (!selectedFramework) return;

    setSaving(true);
    try {
      await api.patch(`/admin/frameworks/${selectedFramework.id}`, {
        name: formData.name,
        version: formData.version,
        description: formData.description
      });
      toast.success('Framework bijgewerkt');
      setShowEditModal(false);
      fetchFrameworks();
    } catch (error) {
      toast.error(getUserErrorMessage(error, 'Bijwerken mislukt'));
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteFramework = async (frameworkId) => {
    if (!window.confirm('Weet u zeker dat u dit framework wilt verwijderen?')) {
      return;
    }

    try {
      await api.delete(`/admin/frameworks/${frameworkId}`);
      toast.success('Framework verwijderd');
      setSelectedFramework(null);
      fetchFrameworks();
    } catch (error) {
      toast.error(getUserErrorMessage(error, 'Verwijderen mislukt'));
    }
  };

  const handleAddControl = async () => {
    if (!selectedFramework || !controlForm.code || !controlForm.name || !controlForm.category || !controlForm.description) {
      toast.error('Vul alle velden in');
      return;
    }

    setSaving(true);
    try {
      await api.post(`/admin/frameworks/${selectedFramework.id}/controls`, controlForm);
      toast.success('Control toegevoegd');
      setShowControlModal(false);
      resetControlForm();
      
      // Refresh framework details
      const response = await api.get(`/admin/frameworks/${selectedFramework.id}`);
      setSelectedFramework(response.data);
      fetchFrameworks();
    } catch (error) {
      toast.error(getUserErrorMessage(error, 'Toevoegen mislukt'));
    } finally {
      setSaving(false);
    }
  };

  const handleUpdateControl = async () => {
    if (!selectedFramework || !editingControl) return;

    setSaving(true);
    try {
      await api.patch(`/admin/frameworks/${selectedFramework.id}/controls/${editingControl.id}`, controlForm);
      toast.success('Control bijgewerkt');
      setShowControlModal(false);
      setEditingControl(null);
      resetControlForm();
      
      // Refresh framework details
      const response = await api.get(`/admin/frameworks/${selectedFramework.id}`);
      setSelectedFramework(response.data);
      fetchFrameworks();
    } catch (error) {
      toast.error(getUserErrorMessage(error, 'Bijwerken mislukt'));
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteControl = async (controlId) => {
    if (!selectedFramework) return;
    if (!window.confirm('Weet u zeker dat u deze control wilt verwijderen?')) {
      return;
    }

    try {
      await api.delete(`/admin/frameworks/${selectedFramework.id}/controls/${controlId}`);
      toast.success('Control verwijderd');
      
      // Refresh framework details
      const response = await api.get(`/admin/frameworks/${selectedFramework.id}`);
      setSelectedFramework(response.data);
      fetchFrameworks();
    } catch (error) {
      toast.error(getUserErrorMessage(error, 'Verwijderen mislukt'));
    }
  };

  const openEditFramework = (framework) => {
    setSelectedFramework(framework);
    setFormData({
      name: framework.name,
      version: framework.version,
      description: framework.description
    });
    setShowEditModal(true);
  };

  const openAddControl = () => {
    setEditingControl(null);
    resetControlForm();
    setShowControlModal(true);
  };

  const openEditControl = (control) => {
    setEditingControl(control);
    setControlForm({
      code: control.code,
      name: control.name,
      category: control.category,
      description: control.description
    });
    setShowControlModal(true);
  };

  const resetForm = () => {
    setFormData({ name: '', version: '', description: '' });
  };

  const resetControlForm = () => {
    setControlForm({ code: '', name: '', category: '', description: '' });
  };

  const selectFramework = async (framework) => {
    try {
      const response = await api.get(`/admin/frameworks/${framework.id}`);
      setSelectedFramework(response.data);
    } catch (error) {
      toast.error('Framework details laden mislukt');
    }
  };

  // Group controls by category
  const groupedControls = selectedFramework?.controls?.reduce((acc, control) => {
    const category = control.category || 'Overig';
    if (!acc[category]) {
      acc[category] = [];
    }
    acc[category].push(control);
    return acc;
  }, {}) || {};

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center h-[calc(100vh-4rem)]">
          <Loader2 className="h-8 w-8 animate-spin text-neutral-400" strokeWidth={1.5} />
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="p-6 lg:p-8 space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <p className="text-xs font-bold uppercase tracking-wider text-neutral-400 mb-1">
              Admin
            </p>
            <h1 className="text-2xl font-bold tracking-tight text-black">
              Framework Builder
            </h1>
            <p className="text-sm text-neutral-600 mt-1">
              Beheer compliance frameworks en controls
            </p>
          </div>
          <Button 
            className="bg-black text-white hover:bg-neutral-800"
            onClick={() => {
              resetForm();
              setShowCreateModal(true);
            }}
            data-testid="create-framework-button"
          >
            <Plus className="mr-2 h-4 w-4" strokeWidth={1.5} />
            Nieuw Framework
          </Button>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Frameworks List */}
          <Card className="border-neutral-200 shadow-[0_2px_4px_rgba(0,0,0,0.04)]">
            <CardHeader className="pb-2">
              <CardTitle className="text-xs font-bold uppercase tracking-wider text-neutral-400">
                Frameworks ({frameworks.length})
              </CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              <div className="divide-y divide-neutral-100">
                {frameworks.map((framework) => (
                  <button
                    key={framework.id}
                    onClick={() => selectFramework(framework)}
                    className={`w-full flex items-center gap-3 p-4 text-left transition-colors ${
                      selectedFramework?.id === framework.id
                        ? 'bg-neutral-100'
                        : 'hover:bg-neutral-50'
                    }`}
                    data-testid={`framework-${framework.id}`}
                  >
                    <div className={`h-10 w-10 rounded-lg flex items-center justify-center ${
                      framework.is_builtin ? 'bg-blue-50' : 'bg-green-50'
                    }`}>
                      {framework.is_builtin ? (
                        <Lock className="h-5 w-5 text-blue-600" strokeWidth={1.5} />
                      ) : (
                        <FileText className="h-5 w-5 text-green-600" strokeWidth={1.5} />
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-black">{framework.name}</p>
                      <p className="text-xs text-neutral-500">
                        {framework.version} • {framework.control_count} controls
                      </p>
                    </div>
                    <ChevronRight className="h-4 w-4 text-neutral-400" strokeWidth={1.5} />
                  </button>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* Framework Details */}
          <div className="lg:col-span-2">
            {selectedFramework ? (
              <Card className="border-neutral-200 shadow-[0_2px_4px_rgba(0,0,0,0.04)]">
                <CardHeader className="flex flex-row items-start justify-between">
                  <div>
                    <div className="flex items-center gap-2 mb-1">
                      <CardTitle className="text-lg font-semibold">
                        {selectedFramework.name} {selectedFramework.version}
                      </CardTitle>
                      {selectedFramework.is_builtin ? (
                        <Badge className="border-blue-200 bg-blue-50 text-blue-700">
                          Ingebouwd
                        </Badge>
                      ) : (
                        <Badge className="border-green-200 bg-green-50 text-green-700">
                          Custom
                        </Badge>
                      )}
                    </div>
                    <CardDescription>{selectedFramework.description}</CardDescription>
                  </div>
                  {!selectedFramework.is_builtin && (
                    <div className="flex gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        className="border-neutral-200"
                        onClick={() => openEditFramework(selectedFramework)}
                      >
                        <Edit className="h-4 w-4" strokeWidth={1.5} />
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        className="border-red-200 text-red-600 hover:bg-red-50"
                        onClick={() => handleDeleteFramework(selectedFramework.id)}
                      >
                        <Trash2 className="h-4 w-4" strokeWidth={1.5} />
                      </Button>
                    </div>
                  )}
                </CardHeader>
                <CardContent>
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-sm font-medium text-black">
                      Controls ({selectedFramework.controls?.length || 0})
                    </h3>
                    {!selectedFramework.is_builtin && (
                      <Button
                        variant="outline"
                        size="sm"
                        className="border-neutral-200"
                        onClick={openAddControl}
                        data-testid="add-control-button"
                      >
                        <Plus className="mr-1 h-3 w-3" strokeWidth={1.5} />
                        Control Toevoegen
                      </Button>
                    )}
                  </div>

                  {Object.keys(groupedControls).length > 0 ? (
                    <Accordion type="multiple" className="space-y-2">
                      {Object.entries(groupedControls).map(([category, controls]) => (
                        <AccordionItem
                          key={category}
                          value={category}
                          className="border border-neutral-200 rounded-lg overflow-hidden"
                        >
                          <AccordionTrigger className="px-4 py-3 hover:bg-neutral-50">
                            <div className="flex items-center gap-2">
                              <Shield className="h-4 w-4 text-neutral-500" strokeWidth={1.5} />
                              <span className="text-sm font-medium">{category}</span>
                              <Badge variant="outline" className="ml-2 border-neutral-200 text-neutral-500">
                                {controls.length}
                              </Badge>
                            </div>
                          </AccordionTrigger>
                          <AccordionContent className="px-4 pb-4">
                            <div className="space-y-2">
                              {controls.map((control) => (
                                <div
                                  key={control.id}
                                  className="flex items-start gap-3 p-3 bg-neutral-50 rounded-lg"
                                >
                                  <div className="flex-1 min-w-0">
                                    <div className="flex items-center gap-2">
                                      <Badge variant="outline" className="border-neutral-300 text-neutral-600 text-xs">
                                        {control.code}
                                      </Badge>
                                      <p className="text-sm font-medium text-black">{control.name}</p>
                                    </div>
                                    <p className="text-xs text-neutral-500 mt-1">{control.description}</p>
                                  </div>
                                  {!selectedFramework.is_builtin && (
                                    <div className="flex gap-1">
                                      <Button
                                        variant="ghost"
                                        size="sm"
                                        className="h-8 w-8 p-0"
                                        onClick={() => openEditControl(control)}
                                      >
                                        <Edit className="h-3 w-3" strokeWidth={1.5} />
                                      </Button>
                                      <Button
                                        variant="ghost"
                                        size="sm"
                                        className="h-8 w-8 p-0 text-red-500 hover:text-red-700"
                                        onClick={() => handleDeleteControl(control.id)}
                                      >
                                        <Trash2 className="h-3 w-3" strokeWidth={1.5} />
                                      </Button>
                                    </div>
                                  )}
                                </div>
                              ))}
                            </div>
                          </AccordionContent>
                        </AccordionItem>
                      ))}
                    </Accordion>
                  ) : (
                    <div className="py-8 text-center bg-neutral-50 rounded-lg">
                      <Shield className="h-12 w-12 text-neutral-300 mx-auto mb-2" strokeWidth={1.5} />
                      <p className="text-neutral-500">Geen controls</p>
                      {!selectedFramework.is_builtin && (
                        <Button
                          variant="outline"
                          size="sm"
                          className="mt-4 border-neutral-200"
                          onClick={openAddControl}
                        >
                          <Plus className="mr-1 h-3 w-3" strokeWidth={1.5} />
                          Eerste Control
                        </Button>
                      )}
                    </div>
                  )}
                </CardContent>
              </Card>
            ) : (
              <Card className="border-neutral-200 shadow-[0_2px_4px_rgba(0,0,0,0.04)]">
                <CardContent className="py-16 text-center">
                  <FileText className="h-12 w-12 text-neutral-300 mx-auto mb-4" strokeWidth={1.5} />
                  <h3 className="text-lg font-medium text-black mb-1">Selecteer een Framework</h3>
                  <p className="text-sm text-neutral-500">
                    Klik op een framework om de controls te bekijken en bewerken
                  </p>
                </CardContent>
              </Card>
            )}
          </div>
        </div>
      </div>

      {/* Create Framework Modal */}
      <Dialog open={showCreateModal} onOpenChange={setShowCreateModal}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Nieuw Framework</DialogTitle>
            <DialogDescription>
              Maak een nieuw compliance framework aan
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="name">Naam</Label>
              <Input
                id="name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="bijv. SOC 2"
                data-testid="framework-name-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="version">Versie</Label>
              <Input
                id="version"
                value={formData.version}
                onChange={(e) => setFormData({ ...formData, version: e.target.value })}
                placeholder="bijv. 2023"
                data-testid="framework-version-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="description">Beschrijving</Label>
              <Textarea
                id="description"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder="Beschrijf het framework en het doel..."
                rows={3}
                data-testid="framework-description-input"
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreateModal(false)}>
              Annuleren
            </Button>
            <Button 
              className="bg-black text-white hover:bg-neutral-800"
              onClick={handleCreateFramework}
              disabled={saving}
              data-testid="create-framework-submit"
            >
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Aanmaken'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Framework Modal */}
      <Dialog open={showEditModal} onOpenChange={setShowEditModal}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Framework Bewerken</DialogTitle>
            <DialogDescription>
              Pas de framework gegevens aan
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="edit-name">Naam</Label>
              <Input
                id="edit-name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="edit-version">Versie</Label>
              <Input
                id="edit-version"
                value={formData.version}
                onChange={(e) => setFormData({ ...formData, version: e.target.value })}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="edit-description">Beschrijving</Label>
              <Textarea
                id="edit-description"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                rows={3}
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEditModal(false)}>
              Annuleren
            </Button>
            <Button 
              className="bg-black text-white hover:bg-neutral-800"
              onClick={handleUpdateFramework}
              disabled={saving}
            >
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Opslaan'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add/Edit Control Modal */}
      <Dialog open={showControlModal} onOpenChange={setShowControlModal}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>{editingControl ? 'Control Bewerken' : 'Nieuwe Control'}</DialogTitle>
            <DialogDescription>
              {editingControl ? 'Pas de control aan' : 'Voeg een nieuwe control toe aan het framework'}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="control-code">Code</Label>
                <Input
                  id="control-code"
                  value={controlForm.code}
                  onChange={(e) => setControlForm({ ...controlForm, code: e.target.value })}
                  placeholder="bijv. CC1.1"
                  data-testid="control-code-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="control-category">Categorie</Label>
                <Input
                  id="control-category"
                  value={controlForm.category}
                  onChange={(e) => setControlForm({ ...controlForm, category: e.target.value })}
                  placeholder="bijv. Governance"
                  data-testid="control-category-input"
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="control-name">Naam</Label>
              <Input
                id="control-name"
                value={controlForm.name}
                onChange={(e) => setControlForm({ ...controlForm, name: e.target.value })}
                placeholder="bijv. Toegangsbeheer"
                data-testid="control-name-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="control-description">Beschrijving</Label>
              <Textarea
                id="control-description"
                value={controlForm.description}
                onChange={(e) => setControlForm({ ...controlForm, description: e.target.value })}
                placeholder="Beschrijf de control..."
                rows={3}
                data-testid="control-description-input"
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => {
              setShowControlModal(false);
              setEditingControl(null);
              resetControlForm();
            }}>
              Annuleren
            </Button>
            <Button 
              className="bg-black text-white hover:bg-neutral-800"
              onClick={editingControl ? handleUpdateControl : handleAddControl}
              disabled={saving}
              data-testid="control-submit"
            >
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : (editingControl ? 'Opslaan' : 'Toevoegen')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </AdminLayout>
  );
};

export default AdminFrameworks;
